import React from 'react';
import TranscriptEntry from './TranscriptEntry';

/**
 * Keeps the server's order for conversation entries, with linked answers
 * displayed directly under their question. Pair by identity, never by text or
 * position: a batch can be answered in a different order from the questions.
 */
export default function Transcript({
  rows, onExportDocx, exporting, composerTargetId, onOpenDocument,
  exportFormats, onExportArtifact, busyExport,
  administrative = false, onOpenAttachment, completed = false,
}) {
  const questionIds = new Set(rows
    .filter(entry => entry.kind === 'agent_question' && entry.human_request_id)
    .map(entry => entry.human_request_id));
  const answersByQuestion = new Map();
  for (const entry of rows) {
    if (entry.kind !== 'user_answer' || !questionIds.has(entry.human_request_id)) continue;
    const answers = answersByQuestion.get(entry.human_request_id) || [];
    answers.push(entry);
    answersByQuestion.set(entry.human_request_id, answers);
  }

  // Requested output documents stay in the conversation, including while
  // writing. Intermediate research artifacts remain in the materials panel.
  const visibleRows = administrative ? rows : rows.filter(entry => {
    if (entry.kind === 'deliverable') return Boolean(entry.output_id);
    return !(completed && entry.kind === 'assistant_message' && entry.message_type === 'progress');
  });
  const deliveries = new Map(visibleRows.filter(entry => entry.run_id && entry.message_type === 'delivery')
    .map(entry => [entry.run_id, entry.id]));
  const documents = new Map();
  const conversationRows = visibleRows.filter(entry => {
    if (administrative || !deliveries.has(entry.run_id)
      || !['deliverable', 'final_report'].includes(entry.kind)) return true;
    const deliveryId = deliveries.get(entry.run_id);
    documents.set(deliveryId, [...(documents.get(deliveryId) || []), entry]);
    return false;
  }).flatMap(entry => [entry, ...(documents.get(entry.id) || [])]);

  const renderEntry = entry => (
    <TranscriptEntry
      key={entry.id}
      entry={entry}
      administrative={administrative}
      onOpenAttachment={onOpenAttachment}
      onOpenDocument={onOpenDocument}
      onExportDocx={entry.kind === 'final_report' ? onExportDocx : undefined}
      exporting={exporting}
      exportFormats={exportFormats}
      onExportArtifact={onExportArtifact}
      busyExport={busyExport}
      ownedByComposer={
        entry.kind === 'agent_question'
        && composerTargetId != null
        && entry.human_request_id === composerTargetId
      }
    />
  );

  return conversationRows.map(entry => {
    if (entry.kind === 'user_answer' && answersByQuestion.has(entry.human_request_id)) return null;
    const answers = entry.kind === 'agent_question' && answersByQuestion.get(entry.human_request_id);
    if (!answers) return renderEntry(entry);
    return (
      <div key={entry.id} className="chat-hr-pair" role="group" aria-label="Вопрос и ответ">
        {renderEntry(entry)}
        {answers.map(renderEntry)}
      </div>
    );
  });
}
